import React, { useState, useEffect, useContext, useRef, useCallback, memo } from 'react';
import api from '../../api';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';
import TileSelectionModal from '../../components/TileSelectionModal';
import MasterSelection from '../../components/MasterSelection';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { 
  FaPlus, FaEdit, FaEye, FaSearch, FaTimes, FaFilter, FaFileExport,
  FaShoppingCart, FaClock, FaCheckCircle, FaBan, FaBox, FaCalendarAlt, FaUser
} from 'react-icons/fa';

// Format number with commas
const formatNumberWithCommas = (number) => {
  const value = typeof number === 'string' ? parseFloat(number) : number;
  if (!value || isNaN(value)) return '0.00';
  return new Intl.NumberFormat('en-IN', {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2
  }).format(value);
};

// Initialize form state function
const initializeFormState = (userId = '') => ({
  supplier: null,
  items: [{
    tileId: '',
    tilesName: '',
    orderedQuantityBoxes: 0,
    unitPricePerBox: 0,
    totalAmount: 0,
    tileDetails: null
  }],
  expectedDeliveryDate: '',
  remarks: '',
  createdBy: userId
});

const TilesOrderEnhanced = () => {
  const { user } = useContext(AuthContext);

  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('darkMode') === 'true');

  // Sync dark mode with localStorage changes
  useEffect(() => {
    const checkDarkMode = () => {
      const isDark = localStorage.getItem('darkMode') === 'true';
      setDarkMode(isDark);
    };
    const interval = setInterval(checkDarkMode, 500);
    return () => clearInterval(interval);
  }, []);
  
  // Generate unique ID for items (used when item doesn't have an existing _id)
  const generateId = () => `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  // Initialize refs
  const formRef = useRef(null);
  const calculationInProgress = useRef(false);
  
  // Data source states
  const [suppliers, setSuppliers] = useState([]);
  const [orders, setOrders] = useState([]);
  
  // Order states
  const [selectedOrderFilter, setSelectedOrderFilter] = useState(null);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [orderSearchTerm, setOrderSearchTerm] = useState('');
  const [dateRangeFilter, setDateRangeFilter] = useState({ startDate: '', endDate: '' });
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Basic states
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [orderId, setOrderId] = useState(null);

  // Form state
  const [form, setForm] = useState(() => initializeFormState(user?.user?._id));

  // Ensure form.items is always an array
  useEffect(() => {
    if (!form.items || !Array.isArray(form.items)) {
      setForm(prev => ({
        ...prev,
        items: [initializeFormState().items[0]]
      }));
    }
  }, []);

  // Tiles selection states (Enhanced Modal System)
  const [showTilesModal, setShowTilesModal] = useState(false);
  const [currentItemIndex, setCurrentItemIndex] = useState(0);

  // Supplier selection modal states
  const [showSupplierModal, setShowSupplierModal] = useState(false);

  // View order modal states
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);



  // Pagination for orders
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrev: false
  });

  // Status filter
  const [statusFilter, setStatusFilter] = useState('');

  // Load initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Load orders when filters change
  useEffect(() => {
    fetchOrders();
  }, [pagination.currentPage, statusFilter, selectedOrderFilter, dateRangeFilter]);

  // Filter orders based on search term
  useEffect(() => {
    if (!orderSearchTerm.trim()) {
      setFilteredOrders(orders);
      return;
    }

    const filtered = orders.filter(order => 
      order.orderId?.toLowerCase().includes(orderSearchTerm.toLowerCase()) ||
      order.supplier?.name?.toLowerCase().includes(orderSearchTerm.toLowerCase()) ||
      order.status?.toLowerCase().includes(orderSearchTerm.toLowerCase())
    );
    setFilteredOrders(filtered);
  }, [orderSearchTerm, orders]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const userCompanyId = user?.user?.company?._id || user?.user?.company;
      const [suppliersRes] = await Promise.all([
        api.get('/suppliers/all', { params: { userCompanyId } }).catch(() => ({ data: [] }))
      ]);

      console.log('🔄 Suppliers API Response:', {
        status: suppliersRes?.status,
        dataType: typeof suppliersRes?.data,
        hasData: !!suppliersRes?.data,
        isArray: Array.isArray(suppliersRes?.data),
        dataLength: suppliersRes?.data?.length || 0,
        sample: suppliersRes?.data?.[0]
      });

      // Handle different response formats
      let suppliersData = [];
      if (suppliersRes?.data?.data) {
        // Response has nested data property
        suppliersData = Array.isArray(suppliersRes.data.data) ? suppliersRes.data.data : [];
      } else if (Array.isArray(suppliersRes?.data)) {
        // Response data is directly an array
        suppliersData = suppliersRes.data;
      }

      setSuppliers(suppliersData);

      console.log('✅ Initial data loaded:', {
        suppliers: suppliersData.length || 0,
        supplierNames: suppliersData.map(s => s.name).join(', ')
      });
    } catch (error) {
      console.error('❌ Error loading initial data:', error);
      Swal.fire('Error', 'Failed to load initial data. Please refresh the page.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      let params = {
        page: pagination.currentPage,
        limit: 10
      };
      
      if (user?.user?.company?._id) params.company = user.user.company._id;
      if (statusFilter) params.status = statusFilter;
      if (selectedOrderFilter) params.supplier = selectedOrderFilter.value;
      if (dateRangeFilter.startDate) params.startDate = dateRangeFilter.startDate;
      if (dateRangeFilter.endDate) params.endDate = dateRangeFilter.endDate;

      const response = await api.get('/tiles-orders', { params });

      if (response.data) {
        setOrders(response.data.data.orders || []);
        setPagination(response.data.data.pagination || pagination);
      }
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Error fetching orders');
    } finally {
      setLoading(false);
    }
  };

  // Enhanced tile selection handler for modal
  const handleTileSelect = (tile) => {
    const updatedItems = [...form.items];
    
    if (currentItemIndex < updatedItems.length) {
      updatedItems[currentItemIndex] = {
        ...updatedItems[currentItemIndex],
        tileId: tile._id,
        tilesName: tile.tilesName || tile.name,
        unitPricePerBox: tile.pricePerBox || 0,
        tileDetails: tile
      };
      
      // Calculate amount if quantity exists
      if (updatedItems[currentItemIndex].orderedQuantityBoxes > 0) {
        updatedItems[currentItemIndex].totalAmount = 
          updatedItems[currentItemIndex].orderedQuantityBoxes * updatedItems[currentItemIndex].unitPricePerBox;
      }

      setForm(prev => ({ ...prev, items: updatedItems }));
      
      // Calculate amount for this item
      calculateItemAmount(currentItemIndex, updatedItems[currentItemIndex]);
    }
    
    setShowTilesModal(false);
  };

  const calculateItemAmount = (itemIndex, itemData = null) => {
    if (calculationInProgress.current) return;
    
    const item = itemData || form.items[itemIndex];
    if (!item) return;

    const boxes = parseFloat(item.orderedQuantityBoxes) || 0;
    const pricePerBox = parseFloat(item.unitPricePerBox) || 0;
    const totalAmount = boxes * pricePerBox;

    const updatedItems = [...form.items];
    updatedItems[itemIndex] = {
      ...item,
      totalAmount: totalAmount
    };

    setForm(prev => ({
      ...prev,
      items: updatedItems
    }));

    // Calculate total order value
    calculateTotalOrderValue(updatedItems);
  };

  const calculateTotalOrderValue = (items = form.items) => {
    const total = items.reduce((sum, item) => sum + (parseFloat(item.totalAmount) || 0), 0);
    setForm(prev => ({
      ...prev,
      totalOrderValue: total
    }));
  };

  const addItem = () => {
    const newItem = {
      id: generateId(),
      tileId: '',
      tilesName: '',
      orderedQuantityBoxes: 0,
      unitPricePerBox: 0,
      totalAmount: 0,
      tileDetails: null
    };

    setForm(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
  };

  const removeItem = (index) => {
    if (form.items.length <= 1) {
      toast.warning('At least one item is required');
      return;
    }

    const updatedItems = form.items.filter((_, i) => i !== index);
    setForm(prev => ({
      ...prev,
      items: updatedItems
    }));

    calculateTotalOrderValue(updatedItems);
  };

  const updateItemField = (index, field, value) => {
    const updatedItems = [...form.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };

    setForm(prev => ({
      ...prev,
      items: updatedItems
    }));

    // Recalculate if quantity or price changed
    if (field === 'orderedQuantityBoxes' || field === 'unitPricePerBox') {
      calculateItemAmount(index, updatedItems[index]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.supplier) {
      await Swal.fire({
        icon: 'warning',
        title: 'Supplier Required',
        text: 'Please select a supplier',
        confirmButtonColor: '#3B82F6'
      });
      return;
    }

    if (!form.items.length || form.items.some(item => !item.tileId || !item.orderedQuantityBoxes)) {
      await Swal.fire({
        icon: 'warning',
        title: 'Items Required',
        text: 'Please add at least one item with valid tile and quantity',
        confirmButtonColor: '#3B82F6'
      });
      return;
    }

    try {
      setSubmitting(true);

      const orderData = {
        supplier: form.supplier.value,
        items: form.items.map(item => ({
          tileId: item.tileId,
          orderedQuantityBoxes: parseInt(item.orderedQuantityBoxes),
          unitPricePerBox: parseFloat(item.unitPricePerBox),
          totalAmount: parseFloat(item.totalAmount)
        })),
        expectedDeliveryDate: form.expectedDeliveryDate,
        remarks: form.remarks,
        company: user?.user?.company?._id || user?.user?.company,
        createdBy: user?.user?._id
      };

      console.log(`${isEditing ? '✏️ Updating' : '➕ Creating'} order:`, orderData);

      const response = isEditing 
        ? await api.put(`/tiles-orders/${orderId}`, orderData)
        : await api.post('/tiles-orders', orderData);

      console.log('✅ Response:', response.data);

      if (response.data && response.data.success) {
        toast.success(`Tiles order ${isEditing ? 'updated' : 'created'} successfully!`);
        resetForm();
        fetchOrders();
      } else {
        throw new Error(response.data?.message || 'Operation failed');
      }
    } catch (error) {
      console.error('❌ Error saving tiles order:', error);
      
      await Swal.fire({
        icon: 'error',
        title: 'Error Saving Order',
        text: error.response?.data?.message || `Error saving tiles order: ${error.message}`,
        confirmButtonColor: '#3B82F6'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setForm(initializeFormState(user?.user?._id));
    setIsEditing(false);
    setOrderId(null);
    setShowTilesModal(false);
  };

  const handleView = async (order) => {
    try {
      setLoading(true);
      const response = await api.get(`/tiles-orders/${order._id}`);

      if (response.data) {
        setSelectedOrder(response.data.data);
        setShowViewModal(true);
      } else {
        toast.error('Failed to fetch order details');
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      toast.error('Error fetching order details');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (order) => {
    console.log('🎯 EDIT BUTTON CLICKED!', order);
    
    try {
      console.log('📝 Edit clicked for order:', order._id);
      
      // Check if order can be edited
      if (order.status === 'completed' || order.status === 'cancelled' || order.status === 'partial') {
        console.log('❌ Order status prevents editing:', order.status);
        await Swal.fire({
          icon: 'error',
          title: 'Cannot Edit Order',
          text: `Cannot edit ${order.status} orders`,
          confirmButtonColor: '#3B82F6'
        });
        return;
      }

      setLoading(true);
      console.log('🔄 Fetching order details...');
      const response = await api.get(`/tiles-orders/${order._id}`);
      console.log('📦 Order data received:', response.data);

      if (response.data && response.data.success) {
        const orderData = response.data.data;
        
        // For partial orders, we can edit items that still have pending quantities
        // Filter out items that are fully received
        const editableItems = orderData.items?.filter(item => {
          const receivedBoxes = item.receivedQuantityBoxes || 0;
          const orderedBoxes = item.orderedQuantityBoxes || 0;
          return receivedBoxes < orderedBoxes; // Still has pending quantity
        });
        
        if (!editableItems || editableItems.length === 0) {
          await Swal.fire({
            icon: 'error',
            title: 'Cannot Edit Order',
            text: 'Cannot edit order - all items have been fully received.',
            confirmButtonColor: '#3B82F6'
          });
          setLoading(false);
          return;
        }
        
        // Show info if some items are not editable
        if (editableItems.length < orderData.items?.length) {
          toast.info('Note: Only items with pending quantities can be edited. Fully received items are excluded.');
        }
        
        console.log('👥 Available suppliers:', suppliers.length);
        console.log('🔍 Looking for supplier:', orderData.supplier);
        
        // Find supplier - handle both populated and non-populated cases
        const supplierId = typeof orderData.supplier === 'object' ? orderData.supplier?._id : orderData.supplier;
        const supplierName = typeof orderData.supplier === 'object' ? orderData.supplier?.name : 'Unknown Supplier';
        const foundSupplier = suppliers.find(s => s._id === supplierId);
        
        console.log('✅ Found supplier:', foundSupplier, 'ID:', supplierId);
        
        const formData = {
          supplier: foundSupplier ? 
                   { value: foundSupplier._id, label: foundSupplier.name } : 
                   { value: supplierId, label: supplierName },
          items: editableItems.map((item, index) => {
            console.log(`📦 Processing editable item ${index}:`, item);
            
            // Handle both populated and non-populated tileId
            const tileId = typeof item.tileId === 'object' ? item.tileId._id : item.tileId;
            const tileName = typeof item.tileId === 'object' ? item.tileId.tilesName : `Tile ${tileId}`;
            const tileDetails = typeof item.tileId === 'object' ? item.tileId : null;
            
            return {
              id: item._id || generateId(),
              tileId: tileId,
              tilesName: tileName,
              orderedQuantityBoxes: item.orderedQuantityBoxes,
              unitPricePerBox: item.unitPricePerBox,
              totalAmount: item.totalAmount,
              tileDetails: tileDetails
            };
          }),
          expectedDeliveryDate: orderData.expectedDeliveryDate ? 
                               new Date(orderData.expectedDeliveryDate).toISOString().split('T')[0] : '',
          remarks: orderData.remarks || '',
          totalOrderValue: orderData.totalOrderValue || 0,
          createdBy: user?.user?._id
        };
        
        console.log('📝 Setting form data:', formData);
        setForm(formData);
        
        setIsEditing(true);
        setOrderId(order._id);
        
        console.log('✅ Edit mode activated');
        
        // Scroll to form
        setTimeout(() => {
          if (formRef.current) {
            formRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      } else {
        await Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch order details',
          confirmButtonColor: '#3B82F6'
        });
      }
    } catch (error) {
      console.error('❌ Error fetching order details:', error);
      
      // Check if error is from backend validation
      await Swal.fire({
        icon: 'error',
        title: 'Error Fetching Order',
        text: error.response?.data?.message || `Error fetching order details: ${error.message}`,
        confirmButtonColor: '#3B82F6'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async (order) => {
    try {
      // Check if order can be cancelled
      if (order.status === 'completed' || order.status === 'cancelled') {
        toast.error(`Order is already ${order.status}`);
        return;
      }

      const result = await Swal.fire({
        title: 'Cancel Order?',
        text: 'Are you sure you want to cancel this order?',
        icon: 'warning',
        input: 'textarea',
        inputLabel: 'Cancellation Reason',
        inputPlaceholder: 'Please enter the reason for cancellation...',
        inputValidator: (value) => {
          if (!value) {
            return 'Please enter a cancellation reason';
          }
        },
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, cancel order',
        cancelButtonText: 'No, keep it'
      });

      if (result.isConfirmed && result.value) {
        const response = await api.put(`/tiles-orders/${order._id}/status`, {
          status: 'cancelled',
          cancelReason: result.value,
          cancelledBy: user?.user?._id
        });

        if (response.data?.success) {
          toast.success('Order cancelled successfully');
          fetchOrders();
        } else {
          toast.error(response.data?.message || 'Failed to cancel order');
        }
      }
    } catch (error) {
      console.error('Error cancelling order:', error);
      
      // Check if error is from backend validation
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error('Error cancelling order');
      }
    }
  };

  const updateOrderStatus = async (orderId, status, cancelReason = '') => {
    try {
      const response = await api.put(`/tiles-orders/${orderId}/status`, { status, cancelReason });

      if (response.data) {
        toast.success(`Order ${status} successfully`);
        fetchOrders();
      }
    } catch (error) {
      console.error('Error updating order status:', error);
      toast.error('Error updating order status');
    }
  };

  // Helper function to check if order can be edited or cancelled
  const canEditOrder = (order) => {
    // Only allow editing/cancelling orders with 'pending' status
    // Once dispatch is created, order cannot be edited
    if (order.status !== 'pending') {
      return false;
    }
    
    return true;
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: FaClock },
      'in-transit': { color: 'bg-blue-100 text-blue-800', icon: FaShoppingCart },
      partial: { color: 'bg-orange-100 text-orange-800', icon: FaBox },
      completed: { color: 'bg-green-100 text-green-800', icon: FaCheckCircle },
      cancelled: { color: 'bg-red-100 text-red-800', icon: FaBan }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const IconComponent = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <IconComponent className="w-3 h-3" />
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  const supplierOptions = suppliers.map(supplier => ({
    value: supplier._id,
    label: supplier.name,
    data: supplier
  }));

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'pending', label: 'Pending' },
    { value: 'in-transit', label: 'In Transit' },
    { value: 'partial', label: 'Partial' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' }
  ];

  // Enhanced custom styles for react-select
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '44px',
      fontSize: '14px',
      borderWidth: '2px',
      borderColor: state.isFocused ? '#3b82f6' : darkMode ? '#4b5563' : '#e5e7eb',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
      borderRadius: '10px',
      backgroundColor: darkMode ? '#374151' : state.isFocused ? '#fefefe' : '#ffffff',
      color: darkMode ? '#f3f4f6' : '#1f2937',
      transition: 'all 0.2s ease',
      '&:hover': {
        borderColor: '#3b82f6',
        backgroundColor: darkMode ? '#374151' : '#fefefe'
      }
    }),
    option: (base, state) => ({
      ...base,
      fontSize: '14px',
      padding: '10px 12px',
      backgroundColor: state.isSelected 
        ? '#3b82f6' 
        : state.isFocused 
          ? darkMode ? '#374151' : '#eff6ff' 
          : darkMode ? '#1f2937' : 'white',
      color: state.isSelected ? 'white' : darkMode ? '#f3f4f6' : '#374151',
      '&:hover': {
        backgroundColor: state.isSelected ? '#3b82f6' : darkMode ? '#374151' : '#eff6ff'
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: darkMode ? '#9ca3af' : '#9ca3af',
      fontWeight: '400'
    }),
    singleValue: (base) => ({
      ...base,
      color: darkMode ? '#f3f4f6' : '#1f2937',
      fontWeight: '500'
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '10px',
      border: darkMode ? '2px solid #4b5563' : '2px solid #e5e7eb',
      backgroundColor: darkMode ? '#1f2937' : 'white',
      boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
    }),
    menuList: (base) => ({
      ...base,
      borderRadius: '8px'
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: darkMode ? '#9ca3af' : '#6b7280',
      '&:hover': {
        color: '#3b82f6'
      }
    }),
    input: (base) => ({
      ...base,
      color: darkMode ? '#f3f4f6' : '#1f2937'
    })
  };

  // Loading state with enhanced UI
  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50'}`}>
        <div className="text-center">
          <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-200"></div>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent absolute top-0"></div>
          </div>
          <p className={`mt-4 text-lg ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Loading suppliers and orders...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-6 transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gradient-to-br from-gray-50 to-gray-100'}`}>
      <div className="w-full mx-auto">
        {/* Header - Similar to Tiles Dispatch Entry */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl shadow-2xl p-8 mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-4 rounded-xl backdrop-blur-sm">
                <FaShoppingCart className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white mb-2">Tiles Order Management</h1>
                <p className="text-indigo-100">Create and manage tiles orders with in-transit tracking</p>
              </div>
            </div>
          </div>
        </div>

      {/* Order Form */}
      <div ref={formRef} className={`rounded-2xl shadow-xl p-8 mb-8 transition-colors duration-300 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <h2 className={`text-2xl font-bold mb-6 flex items-center gap-3 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
          <div className="bg-gradient-to-br from-indigo-500 to-purple-500 p-2 rounded-lg">
            <FaPlus className="w-5 h-5 text-white" />
          </div>
          {isEditing ? 'Edit Tiles Order' : 'Create New Tiles Order'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Supplier and Date Selection */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Supplier <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowSupplierModal(true)}
                  disabled={submitting}
                  className={`w-full px-4 py-3 border-2 rounded-xl text-left transition-all duration-200 flex items-center justify-between group disabled:opacity-50 ${darkMode ? 'bg-gray-700 border-gray-600 hover:border-indigo-400' : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-indigo-200 hover:border-indigo-400'}`}
                >
                  <span className={form.supplier ? (darkMode ? "text-white font-medium" : "text-gray-900 font-medium") : (darkMode ? "text-gray-400" : "text-gray-400")}>
                    {form.supplier ? (
                      <span className="flex items-center gap-2">
                        <FaUser className="text-indigo-600" />
                        {form.supplier.label}
                      </span>
                    ) : (
                      "Click to select supplier..."
                    )}
                  </span>
                  <FaSearch className="text-indigo-600 group-hover:scale-110 transition-transform" />
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <label className={`block text-sm font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Expected Delivery Date
              </label>
              <input
                type="date"
                value={form.expectedDeliveryDate}
                onChange={(e) => setForm(prev => ({ ...prev, expectedDeliveryDate: e.target.value }))}
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-indigo-400 transition-all duration-200 hover:border-indigo-300 ${darkMode ? 'bg-gray-700 border-gray-600 text-white disabled:bg-gray-600' : 'border-gray-200 disabled:bg-gray-50'}`}
                disabled={submitting}
              />
            </div>
          </div>

          {/* Items Section */}
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h3 className={`text-lg font-semibold flex items-center gap-2 ${darkMode ? 'text-white' : 'text-gray-800'}`}>
                <div className="bg-gradient-to-br from-indigo-500 to-purple-500 p-2 rounded-lg">
                  <FaBox className="w-4 h-4 text-white" />
                </div>
                Order Items
              </h3>
              <button
                type="button"
                onClick={addItem}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:from-blue-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                disabled={submitting}
              >
                <FaPlus className="w-4 h-4" />
                Add Item
              </button>
            </div>

            {/* Items Table - Similar to Dispatch Entry */}
            <div className={`overflow-x-auto rounded-xl border-2 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
              <table className="w-full">
                <thead className={`bg-gradient-to-r ${darkMode ? 'from-indigo-800 to-purple-800' : 'from-indigo-600 to-purple-600'} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">#</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Tile Name</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Quantity (Boxes)</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Price/Box (₹)</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Total (₹)</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
                  {form.items.map((item, index) => (
                    <tr key={item.id || index} className={`transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-indigo-50'}`}>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 text-white font-bold text-sm">
                          {index + 1}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 items-center">
                          <div className="flex-1">
                            {item.tilesName ? (
                              <div>
                                <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.tilesName}</div>
                                {item.tileDetails && (
                                  <div className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                                    {item.tileDetails.brand?.name} • {item.tileDetails.tilesCategory?.name} • {item.tileDetails.tilesPiecesPerBox} pcs/box
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className={`italic ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>No tile selected</span>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setCurrentItemIndex(index);
                              setShowTilesModal(true);
                            }}
                            className="px-3 py-2 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-lg hover:from-blue-600 hover:to-indigo-600 transition-all duration-200 flex items-center gap-2 disabled:opacity-50"
                            disabled={submitting}
                          >
                            <FaSearch className="w-3 h-3" />
                            <span className="text-xs font-medium">Browse</span>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={item.orderedQuantityBoxes}
                          onChange={(e) => updateItemField(index, 'orderedQuantityBoxes', e.target.value)}
                          className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-indigo-400 transition-all text-center font-semibold ${darkMode ? 'bg-gray-700 border-gray-500 text-white disabled:bg-gray-600' : 'border-gray-300 disabled:bg-gray-50'}`}
                          disabled={submitting}
                          placeholder="0"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={item.unitPricePerBox}
                          onChange={(e) => updateItemField(index, 'unitPricePerBox', e.target.value)}
                          className={`w-full px-3 py-2 border-2 rounded-lg focus:outline-none focus:border-indigo-400 transition-all text-center font-semibold ${darkMode ? 'bg-gray-700 border-gray-500 text-white disabled:bg-gray-600' : 'border-gray-300 disabled:bg-gray-50'}`}
                          disabled={submitting}
                          placeholder="0.00"
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span className={`inline-block px-3 py-1 rounded-lg font-bold ${darkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800'}`}>
                          ₹{formatNumberWithCommas(item.totalAmount)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          className="px-3 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
                          disabled={submitting || form.items.length <= 1}
                          title={form.items.length <= 1 ? "Cannot remove the last item" : "Remove item"}
                        >
                          <FaTimes className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Order Total */}
            <div className={`mt-6 p-6 rounded-xl border-2 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200'}`}>
              <div className="flex justify-between items-center">
                <span className={`text-lg font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Total Order Value:</span>
                <span className="text-2xl font-black bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  {formatCurrency(form.totalOrderValue)}
                </span>
              </div>
            </div>
          </div>

          {/* Remarks */}
          <div className={`p-6 rounded-xl border-2 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
            <label className={`block text-sm font-bold mb-3 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>
              Remarks / Additional Notes
            </label>
            <textarea
              value={form.remarks}
              onChange={(e) => setForm(prev => ({ ...prev, remarks: e.target.value }))}
              rows={4}
              className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:border-indigo-400 transition-all resize-none ${darkMode ? 'bg-gray-600 border-gray-500 text-white placeholder-gray-400' : 'border-gray-300 bg-white'}`}
              placeholder="Enter any additional remarks or special instructions..."
              disabled={submitting}
            />
          </div>

          {/* Submit Button */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={resetForm}
              className={`px-6 py-3 border-2 rounded-xl transition-all font-semibold disabled:opacity-50 ${darkMode ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'}`}
              disabled={submitting}
            >
              Reset Form
            </button>
            <button
              type="submit"
              className="px-8 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-bold"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <div className="w-5 h-5 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                  Processing...
                </>
              ) : (
                <>
                  <FaShoppingCart className="w-5 h-5" />
                  {isEditing ? 'Update Order' : 'Create Order'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Orders List */}
      <div className={`rounded-2xl shadow-xl border-2 ${darkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'}`}>
        <div className={`p-6 border-b-2 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Orders List
            </h2>
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg hover:from-indigo-600 hover:to-purple-600 transition-all shadow-md"
            >
              <FaFilter className="w-4 h-4" />
              Filters
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex-1 min-w-64">
              <div className="relative">
                <FaSearch className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  type="text"
                  placeholder="Search orders..."
                  value={orderSearchTerm}
                  onChange={(e) => setOrderSearchTerm(e.target.value)}
                  className={`w-full px-4 py-3 pl-12 border-2 rounded-xl focus:outline-none focus:border-indigo-400 transition-all ${darkMode ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' : 'border-gray-300'}`}
                />
              </div>
            </div>
            <div className="min-w-48">
              <Select
                value={statusOptions.find(opt => opt.value === statusFilter)}
                onChange={(selected) => setStatusFilter(selected?.value || '')}
                options={statusOptions}
                placeholder="Filter by status..."
                styles={selectStyles}
                className="react-select-container"
                classNamePrefix="react-select"
                isClearable
                noOptionsMessage={() => "No status options"}
              />
            </div>
          </div>

          {showAdvancedFilters && (
            <div className={`grid grid-cols-1 md:grid-cols-3 gap-4 p-4 rounded-xl border-2 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-indigo-50 border-indigo-200'}`}>
              <div>
                <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Supplier</label>
                <Select
                  value={selectedOrderFilter}
                  onChange={setSelectedOrderFilter}
                  options={supplierOptions}
                  placeholder="Filter by supplier..."
                  styles={selectStyles}
                  isClearable
                  isSearchable
                  className="react-select-container"
                  classNamePrefix="react-select"
                  noOptionsMessage={() => "No suppliers found"}
                />
              </div>
              <div>
                <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>Start Date</label>
                <input
                  type="date"
                  value={dateRangeFilter.startDate}
                  onChange={(e) => setDateRangeFilter(prev => ({ ...prev, startDate: e.target.value }))}
                  className={`w-full px-3 py-2 border-2 rounded-xl focus:outline-none focus:border-indigo-400 ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                />
              </div>
              <div>
                <label className={`block text-sm font-bold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>End Date</label>
                <input
                  type="date"
                  value={dateRangeFilter.endDate}
                  onChange={(e) => setDateRangeFilter(prev => ({ ...prev, endDate: e.target.value }))}
                  className={`w-full px-3 py-2 border-2 rounded-xl focus:outline-none focus:border-indigo-400 ${darkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'}`}
                />
              </div>
            </div>
          )}
        </div>

        {/* Orders Table */}
        <div className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              <div className={`mt-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading orders...</div>
            </div>
          ) : filteredOrders.length > 0 ? (
            <table className="w-full">
              <thead className={`bg-gradient-to-r ${darkMode ? 'from-indigo-800 to-purple-800' : 'from-indigo-600 to-purple-600'} text-white`}>
                <tr>
                  <th className="px-6 py-3 text-left text-sm font-semibold">
                    Order Details
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">
                    Supplier
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">
                    Items
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">
                    Status
                  </th>
                  <th className="px-6 py-3 text-center text-sm font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${darkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
                {filteredOrders.map((order) => (
                  <tr key={order._id} className={`transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-indigo-50'}`}>
                    <td className="px-6 py-4">
                      <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{order.orderId}</div>
                      <div className={`text-sm flex items-center gap-1 mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <FaCalendarAlt className="w-3 h-3" />
                        {formatDate(order.orderDate)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{order.supplier?.name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={darkMode ? 'text-white' : 'text-gray-900'}>{order.items?.length} items</div>
                      <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        Total: {order.items?.reduce((sum, item) => sum + item.orderedQuantityBoxes, 0)} boxes
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {formatCurrency(order.totalOrderValue)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2 justify-center">
                        <button
                          onClick={() => handleView(order)}
                          className="p-2 text-green-600 hover:bg-green-100 rounded-lg transition-all"
                          title="View Order"
                        >
                          <FaEye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEdit(order)}
                          className={`p-2 rounded-lg transition-all ${
                            canEditOrder(order)
                              ? darkMode ? 'text-blue-400 hover:bg-gray-600' : 'text-blue-600 hover:bg-blue-100'
                              : 'text-gray-400 cursor-not-allowed'
                          }`}
                          title={
                            canEditOrder(order)
                              ? 'Edit Order'
                              : `Cannot edit - Order status is ${order.status}`
                          }
                          disabled={!canEditOrder(order)}
                        >
                          <FaEdit className="w-4 h-4" />
                        </button>
                        {canEditOrder(order) && order.status !== 'cancelled' && (
                          <button
                            onClick={() => handleCancelOrder(order)}
                            className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-all"
                            title="Cancel Order"
                          >
                            <FaBan className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12">
              <div className={`text-lg ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>No orders found</div>
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className={`px-6 py-4 border-t-2 flex items-center justify-between ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            <div className={`text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Showing {((pagination.currentPage - 1) * 10) + 1} to {Math.min(pagination.currentPage * 10, pagination.totalItems)} of {pagination.totalItems} results
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                disabled={!pagination.hasPrev}
                className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:from-indigo-600 hover:to-purple-600 transition-all"
              >
                Previous
              </button>
              <span className="px-4 py-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-bold">
                {pagination.currentPage} / {pagination.totalPages}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                disabled={!pagination.hasNext}
                className="px-4 py-2 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:from-indigo-600 hover:to-purple-600 transition-all"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Tiles Selection Modal */}
      <TileSelectionModal
        isOpen={showTilesModal}
        onClose={() => { setShowTilesModal(false); setCurrentItemIndex(0); }}
        onSelect={(tile) => handleTileSelect(tile)}
        darkMode={darkMode}
        userId={user?.user?._id || user?.user?.id}
        showNetStock={true}
      />

      {/* Supplier Selection Modal */}
      <MasterSelection
        isOpen={showSupplierModal}
        onClose={() => setShowSupplierModal(false)}
        onSelect={(supplier) => {
          setForm(prev => ({ ...prev, supplier: { value: supplier._id, label: supplier.name } }));
          setShowSupplierModal(false);
        }}
        allowedTypes={['Supplier']}
        defaultType="Supplier"
        title="Select Supplier"
      />

      {/* View Order Modal */}
      {showViewModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            {/* Header */}
            <div className={`p-6 border-b-2 bg-gradient-to-r ${darkMode ? 'from-indigo-800 to-purple-800 border-gray-600' : 'from-indigo-600 to-purple-600 border-gray-200'} rounded-t-2xl`}>
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-2xl font-bold text-white mb-2">Order Details</h3>
                  <div className="flex items-center gap-4">
                    <span className="text-sm text-white/90">Order ID: <span className="font-semibold">{selectedOrder.orderId}</span></span>
                    {getStatusBadge(selectedOrder.status)}
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedOrder(null);
                  }}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-all"
                >
                  <FaTimes size={24} />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Order Information Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Supplier Information */}
                  <div className={`rounded-xl p-5 border-2 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-indigo-50 border-indigo-200'}`}>
                    <h4 className={`font-bold mb-4 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-2">
                        <FaUser className="w-4 h-4 text-white" />
                      </div>
                      Supplier Information
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Name:</span>
                        <span className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedOrder.supplier?.name || 'N/A'}</span>
                      </div>
                      {selectedOrder.supplier?.email && (
                        <div className="flex justify-between">
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Email:</span>
                          <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedOrder.supplier.email}</span>
                        </div>
                      )}
                      {selectedOrder.supplier?.phone && (
                        <div className="flex justify-between">
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Phone:</span>
                          <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedOrder.supplier.phone}</span>
                        </div>
                      )}
                      {selectedOrder.supplier?.address && (
                        <div className={`pt-2 border-t-2 ${darkMode ? 'border-gray-600' : 'border-indigo-200'}`}>
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Address:</span>
                          <p className={`font-medium mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedOrder.supplier.address}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Order Details */}
                  <div className={`rounded-xl p-5 border-2 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-purple-50 border-purple-200'}`}>
                    <h4 className={`font-bold mb-4 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-2">
                        <FaCalendarAlt className="w-4 h-4 text-white" />
                      </div>
                      Order Details
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Order Date:</span>
                        <span className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatDate(selectedOrder.orderDate)}</span>
                      </div>
                      {selectedOrder.expectedDeliveryDate && (
                        <div className="flex justify-between">
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Expected Delivery:</span>
                          <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatDate(selectedOrder.expectedDeliveryDate)}</span>
                        </div>
                      )}
                      <div className="flex justify-between">
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Total Items:</span>
                        <span className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedOrder.items?.length || 0}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Total Boxes:</span>
                        <span className={`font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                          {selectedOrder.items?.reduce((sum, item) => sum + item.orderedQuantityBoxes, 0) || 0}
                        </span>
                      </div>
                      <div className={`flex justify-between pt-2 border-t-2 ${darkMode ? 'border-gray-600' : 'border-purple-200'}`}>
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Created By:</span>
                        <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedOrder.createdBy?.name || 'N/A'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Order Items */}
                <div className={`rounded-xl p-5 border-2 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                  <h4 className={`font-bold mb-4 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    <div className="w-8 h-8 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center mr-2">
                      <FaBox className="w-4 h-4 text-white" />
                    </div>
                    Order Items
                  </h4>
                  <div className={`overflow-x-auto rounded-xl border-2 ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
                    <table className="w-full">
                      <thead className={`bg-gradient-to-r ${darkMode ? 'from-indigo-800 to-purple-800' : 'from-indigo-600 to-purple-600'} text-white`}>
                        <tr>
                          <th className="text-left py-3 px-4 font-semibold">#</th>
                          <th className="text-left py-3 px-4 font-semibold">Tile Name</th>
                          <th className="text-left py-3 px-4 font-semibold">Tile ID</th>
                          <th className="text-right py-3 px-4 font-semibold">Ordered (Boxes)</th>
                          <th className="text-right py-3 px-4 font-semibold">Received (Boxes)</th>
                          <th className="text-right py-3 px-4 font-semibold">Price/Box</th>
                          <th className="text-right py-3 px-4 font-semibold">Total Amount</th>
                        </tr>
                      </thead>
                      <tbody className={`divide-y ${darkMode ? 'divide-gray-600' : 'divide-gray-200'}`}>
                        {selectedOrder.items?.map((item, index) => (
                          <tr key={item._id || index} className={`transition-colors ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-indigo-50'}`}>
                            <td className={`py-3 px-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{index + 1}</td>
                            <td className={`py-3 px-4 font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.tileId?.tilesName || 'N/A'}</td>
                            <td className={`py-3 px-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>{item.tileId?.id || 'N/A'}</td>
                            <td className={`py-3 px-4 text-right font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{item.orderedQuantityBoxes}</td>
                            <td className="py-3 px-4 text-right font-bold text-green-700">{item.receivedQuantityBoxes || 0}</td>
                            <td className={`py-3 px-4 text-right ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(item.unitPricePerBox)}</td>
                            <td className={`py-3 px-4 text-right font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatCurrency(item.totalAmount)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className={darkMode ? 'bg-gray-600' : 'bg-indigo-50'}>
                        <tr className={`border-t-2 ${darkMode ? 'border-gray-500' : 'border-indigo-300'}`}>
                          <td colSpan="6" className={`py-3 px-4 text-right font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Total Order Value:</td>
                          <td className="py-3 px-4 text-right font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent text-lg">
                            {formatCurrency(selectedOrder.totalOrderValue)}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Remarks */}
                {selectedOrder.remarks && (
                  <div className={`rounded-xl p-5 border-2 ${darkMode ? 'bg-gray-700 border-gray-600' : 'bg-indigo-50 border-indigo-200'}`}>
                    <h4 className={`font-bold mb-3 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      <div className="w-8 h-8 bg-indigo-500 rounded-lg flex items-center justify-center mr-2">
                        <FaEdit className="w-4 h-4 text-white" />
                      </div>
                      Remarks
                    </h4>
                    <p className={`text-sm whitespace-pre-wrap ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>{selectedOrder.remarks}</p>
                  </div>
                )}

                {/* Cancellation Details */}
                {selectedOrder.status === 'cancelled' && selectedOrder.cancelledBy && (
                  <div className={`rounded-xl p-5 border ${darkMode ? 'bg-gradient-to-br from-red-900/30 to-pink-900/30 border-red-800' : 'bg-gradient-to-br from-red-50 to-pink-50 border-red-200'}`}>
                    <h4 className={`font-semibold mb-3 flex items-center ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      <FaBan className="w-4 h-4 mr-2 text-red-600" />
                      Cancellation Details
                    </h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Cancelled By:</span>
                        <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedOrder.cancelledBy?.name || 'N/A'}</span>
                      </div>
                      {selectedOrder.cancelledAt && (
                        <div className="flex justify-between">
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Cancelled At:</span>
                          <span className={`font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>{formatDate(selectedOrder.cancelledAt)}</span>
                        </div>
                      )}
                      {selectedOrder.cancelReason && (
                        <div className={`pt-2 border-t ${darkMode ? 'border-red-800' : 'border-red-200'}`}>
                          <span className={darkMode ? 'text-gray-400' : 'text-gray-600'}>Reason:</span>
                          <p className={`font-medium mt-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{selectedOrder.cancelReason}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className={`p-6 border-t flex justify-between items-center rounded-b-xl ${darkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-200 bg-gray-50'}`}>
              <div className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Last updated: {selectedOrder.updatedAt ? formatDate(selectedOrder.updatedAt) : 'N/A'}
              </div>
              <div className="flex gap-3">
                {canEditOrder(selectedOrder) && selectedOrder.status !== 'cancelled' && (
                  <>
                    <button
                      onClick={() => {
                        setShowViewModal(false);
                        handleEdit(selectedOrder);
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
                    >
                      <FaEdit className="w-4 h-4" />
                      Edit Order
                    </button>
                    <button
                      onClick={() => {
                        setShowViewModal(false);
                        handleCancelOrder(selectedOrder);
                      }}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center gap-2"
                    >
                      <FaBan className="w-4 h-4" />
                      Cancel Order
                    </button>
                  </>
                )}
                {!canEditOrder(selectedOrder) && (
                  <div className="text-sm text-gray-500 italic">
                    {selectedOrder.status === 'cancelled' || selectedOrder.status === 'completed'
                      ? `Order is ${selectedOrder.status}`
                      : 'Items already received - cannot edit or cancel'}
                  </div>
                )}
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedOrder(null);
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      </div>
    </div>
  );
};

export default TilesOrderEnhanced;